import UserChartsApi from './api/user_charts';

async function circlePoints (centerX, centerY, radius) {
  if (!radius) return [];
  try {
    const values = await UserChartsApi.getAsync({ x: centerX, y: centerY, radius });
    return values;
  } catch (error) {
    console.error(error);
    return [];
  }
};
function chartData (headers, redLinePoints, blueLinePoints, pointsData, colorTargetX, colorTargetY) {
  const model = [];
  model.push(headers);
  // Red circle
  for (let item of redLinePoints) {
    model.push([item.x, null, item.y, null, null]);
  }
  // Blue circle
  for (let item of blueLinePoints) {
    model.push([item.x, null, null, item.y, null]);
  }
  // Points
  for (let item of pointsData) {
    model.push([item.x, item.y, null, null, null]);
  }
  // Star
  model.push([colorTargetX || 0, null, null, null, colorTargetY || 0]);

  return model;
}
function options({
  title, xTitle, yTitle,
  xMinimum = -10, xMaximum = 10,
  yMinimum = -10, yMaximum = 10,
  showPoints = true,
  height
}) {
  return {
    title,
    height: height || 300,
    hAxis: {
      viewWindowMode: "explicit",
      viewWindow: {
        min: xMinimum - 2,
        max: xMaximum + 2,
      },
      title: xTitle,
      minValue: xMinimum,
      maxValue: xMaximum,
      gridlines: {
        count: 8
      }
    },
    vAxis: {
      viewWindowMode: "explicit",
      viewWindow: {
        min: yMinimum - 2,
        max: yMaximum + 2,
      },
      title: yTitle,
      minValue: yMinimum,
      maxValue: yMaximum,
      gridlines: {
        count: 8
      }
    },
    legend: {
      position: "bottom",
      alignment: "center",
      textStyle: { fontSize: 8 },
    },
    interpolateNulls: true,
    series: {
      0: { pointSize: showPoints ? 3 : 1, color: "blue" },
      1: { lineWidth: 1, pointSize: 0, color: "red", enableInteractivity: false },
      2: { lineWidth: 1, pointSize: 0, color: "blue", enableInteractivity: false },
      3: { pointShape: { type: "star", sides: 4, dent: 0.2 }, color: "red" },
    },
  };
};

function axisLimits (elements = []) {
  const maximumXs = []; const minimumXs = [];
  const maximumYs = []; const minimumYs = [];
  for (const items of elements) {
    if (items.length) {
      const xs = items.filter(item => item.x != 0 && item.y != 0).map(({x}) => x);
      const ys = items.filter(item => item.x != 0 && item.y != 0).map(({y}) => y);
      //
      maximumXs.push(Math.max(...xs));
      minimumXs.push(Math.min(...xs));
      //
      maximumYs.push(Math.max(...ys));
      minimumYs.push(Math.min(...ys));
    }
  }
  const xMinimum = Math.min(...minimumXs);
  const xMaximum = Math.max(...maximumXs);
  const yMinimum = Math.min(...minimumYs);
  const yMaximum = Math.max(...maximumYs);

  return { xMinimum, xMaximum, yMinimum, yMaximum };
}

export default {
  ab: {
    async build({ entries, nominal, custom, height }) {
      const points = entries.map(({ MeasurementA: x, MeasurementB: y }) => ({x, y}))
      let redCircle = [];
      let blueCircle = [];
      let colorTargetX = nominal.MeasurementA;
      let colorTargetY = nominal.MeasurementB;
      const showCustom = !!(custom && Object.keys(custom).length);
      let red = nominal.MeasurementD, blue = 0;
      if (showCustom) {
        colorTargetX = custom.MeasurementA;
        colorTargetY = custom.MeasurementB;
        // Decide inner: :blue & outer: :red circles
        red = nominal.MeasurementD >= custom.MeasurementD ? nominal.MeasurementD : custom.MeasurementD;
        blue = nominal.MeasurementD >= custom.MeasurementD ? custom.MeasurementD : nominal.MeasurementD;
        blueCircle = await circlePoints(custom.MeasurementA, custom.MeasurementB, blue);
        redCircle = await circlePoints(custom.MeasurementA, custom.MeasurementB, red);
      } else {
        blueCircle.push({x: 0, y: 0});
        redCircle = await circlePoints(nominal.MeasurementA, nominal.MeasurementB, nominal.MeasurementD);
      }
      const showPoints = !!points.length;
      if (!showPoints) points.push({x: 0, y: 0});
      // ΔE*ab {red}, ΔE*ab {blue}
      const headers = ["X", "a°b°", `ΔE*ab ${red}`, `ΔE*ab ${blue}`, "Color Target"];
      const data = chartData(headers, redCircle, blueCircle, points, colorTargetX, colorTargetY);
      const limits = axisLimits([points, redCircle, blueCircle]);
      const chartOptions = options({
        title: "Color Date Plot(a°b°)",
        xTitle: "a°",
        yTitle: "b°",
        showPoints,
        height,
        ...limits
      });
      return { data, options: chartOptions };
    },
  },
  al: {
    async build({ entries, nominal, custom, height }) {
      const points = entries.map(({ MeasurementA: x, MeasurementL: y }) => ({x, y}))
      let redCircle = [];
      let blueCircle = [];
      let colorTargetX = nominal.MeasurementA;
      let colorTargetY = nominal.MeasurementL;
      const showCustom = !!(custom && Object.keys(custom).length);
      let red = nominal.MeasurementD, blue = 0;
      if (showCustom) {
        colorTargetX = custom.MeasurementA;
        colorTargetY = custom.MeasurementL;
        // Decide inner: :blue & outer: :red circles
        red = nominal.MeasurementD >= custom.MeasurementD ? nominal.MeasurementD : custom.MeasurementD;
        blue = nominal.MeasurementD >= custom.MeasurementD ? custom.MeasurementD : nominal.MeasurementD;
        blueCircle = await circlePoints(custom.MeasurementA, custom.MeasurementL, blue);
        redCircle = await circlePoints(custom.MeasurementA, custom.MeasurementL, red);
      } else {
        blueCircle.push({x: 0, y: 0});
        redCircle = await circlePoints(nominal.MeasurementA, nominal.MeasurementL, nominal.MeasurementD);
      }
      const showPoints = !!points.length;
      if (!showPoints) points.push({x: 0, y: 0});
      // ΔE*ab {red}, ΔE*ab {blue}
      const headers = ["X", "L°a°", `DE*La ${red}`, `DE*La ${blue}`, "Color Target"];
      const data = chartData(headers, redCircle, blueCircle, points, colorTargetX, colorTargetY);
      const limits = axisLimits([points, redCircle, blueCircle]);
      const chartOptions = options({
        title: "Color Date Plot(L°a°)",
        xTitle: "a°",
        yTitle: "L°",
        showPoints,
        height,
        ...limits
      });
      return { data, options: chartOptions };
    },
  },
  bl: {
    async build({ entries, nominal, custom, height }) {
      const points = entries.map(({ MeasurementB: x, MeasurementL: y }) => ({x, y}))
      let redCircle = [];
      let blueCircle = [];
      let colorTargetX = nominal.MeasurementB;
      let colorTargetY = nominal.MeasurementL;
      const showCustom = !!(custom && Object.keys(custom).length);
      let red = nominal.MeasurementD, blue = 0;
      if (showCustom) {
        colorTargetX = custom.MeasurementB;
        colorTargetY = custom.MeasurementL;
        // Decide inner: :blue & outer: :red circles
        red = nominal.MeasurementD >= custom.MeasurementD ? nominal.MeasurementD : custom.MeasurementD;
        blue = nominal.MeasurementD >= custom.MeasurementD ? custom.MeasurementD : nominal.MeasurementD;
        blueCircle = await circlePoints(custom.MeasurementB, custom.MeasurementL, blue);
        redCircle = await circlePoints(custom.MeasurementB, custom.MeasurementL, red);
      } else {
        blueCircle.push({x: 0, y: 0});
        redCircle = await circlePoints(nominal.MeasurementB, nominal.MeasurementL, nominal.MeasurementD);
      }
      const showPoints = !!points.length;
      if (!showPoints) points.push({x: 0, y: 0});
      // ΔE*ab {red}, ΔE*ab {blue}
      const headers = ["X", "L°b°", `DE*Lb ${red}`, `DE*Lb ${blue}`, "Color Target"];
      const data = chartData(headers, redCircle, blueCircle, points, colorTargetX, colorTargetY);
      const limits = axisLimits([points, redCircle, blueCircle]);
      const chartOptions = options({
        title: "Color Date Plot(L°b°)",
        xTitle: "b°",
        yTitle: "L°",
        showPoints,
        height,
        ...limits
      });
      return { data, options: chartOptions };
    },
  },
  load({ data, options, elementId }) {
    return new Promise((resolve, reject) => {
      google.charts.setOnLoadCallback(() => {
        const dataTable = google.visualization.arrayToDataTable(data);
        const node = document.getElementById(elementId);
        if (!node) return;
        const chart = new google.visualization.ScatterChart(node);
        chart.draw(dataTable, options);
        resolve(chart);
      });
    });
  }
};
